/** Plate-based V2 of the Bureau & KYC Consent editor — thin config wrapper
    around the shared PlateDocumentDrawer. */
import { useCallback } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { closeBureauKycConsent, setBureauKycConsentData } from '../../store/investor/action';
import { fetchBureauKycConsentTemplate } from '../../store/investor/investorApiHelper';
import { fetchCompanyOverviewData } from '../../store/dashboard/overview/actions';
import { OverviewData } from '../../store/dashboard/overview/types';
import { initializeTemplateWithMetadata } from '../../utils/initializeTemplateWithMetadata';
import { DATE_FORMAT } from '../../utils/constants/commonConstants';
import { AICA_BUREAU_KYC_CONSENT_EDIT } from '../../utils/constants/mixpanelEvents/investorEvents';
import { PlateDocumentDrawer, type BuildInitialTemplateArgs } from '../PlateDocumentEditor/PlateDocumentDrawer';

function BureauKycConsentV2() {
    const dispatch = useAppDispatch();
    const kycData = useAppSelector((state) => state.investor.bureauAndKYCConsentLetter);
    const orgId = kycData.orgId ?? '';
    const companyName = kycData.companyName ?? '';

    const buildInitialTemplate = useCallback(
        ({ orgId, investorOrgId, emailId, companyName, companyMainImg, dispatch }: BuildInitialTemplateArgs) =>
            new Promise<any[]>((resolve, reject) => {
                dispatch(
                    fetchCompanyOverviewData({
                        assesseeOrgId: orgId,
                        investorOrgId,
                        onSuccess: async (data: OverviewData) => {
                            const template = await fetchBureauKycConsentTemplate({
                                accountType: 'INVESTOR',
                                assesseeOrgId: orgId,
                                investorOrgId,
                                primaryEmail: emailId,
                            });
                            resolve(
                                initializeTemplateWithMetadata(template || [], {
                                    pnoLogo: companyMainImg,
                                    companyName: companyName ?? '',
                                    companyAddress: data?.headquarterLocation?.fullAddress ?? '',
                                    userName: '',
                                    userDesignation: '',
                                    userEmail: '',
                                    currentDate: dayjs().format(DATE_FORMAT),
                                }),
                            );
                        },
                        onError: () => reject(new Error('company overview fetch failed')),
                    }),
                );
            }),
        [],
    );

    return (
        <PlateDocumentDrawer
            documentType="BUREAU_KYC_CONSENT"
            title="Bureau & KYC Consent"
            fileNamePrefix="Bureau_KYC_Consent"
            drawerId="bureau-kyc-consent-plate"
            toolbarHostId="bureau-kyc-consent-v2-toolbar-host"
            uploadSource="bureau-kyc-consent"
            imageDocumentType="bureauKycConsent"
            mixpanelSaveEvent={AICA_BUREAU_KYC_CONSENT_EDIT}
            orgId={orgId}
            companyName={companyName}
            loading={!!kycData.loading}
            onClose={() => dispatch(closeBureauKycConsent())}
            setLoading={(loading) => dispatch(setBureauKycConsentData({ loading }))}
            buildInitialTemplate={buildInitialTemplate}
            onComposeAttach={kycData.onComposeAttach}
        />
    );
}

export default BureauKycConsentV2;
